/*!
 * Copyright (c) HSO.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ContentEditableEvent, getUrlSync, RichTextComponent, Text } from '@msdyn365-commerce/core';
import { Address, BusinessPartnerProspect } from '@msdyn365-commerce/retail-proxy';
import { createProspectAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/BusinessPartnersDataActions.g';
import { EmailRegex } from '@msdyn365-commerce-modules/retail-actions';
import { getPayloadObject, getTelemetryAttributes, getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent, Button, Module, Node } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IBusinessSignUpViewProps } from '@msdyn365-commerce-modules/business-account-management/src/modules/business-sign-up/./business-sign-up';

import { SignUpExtensionInput, InputTypeExtension } from './components/sign-up-input';
import { IBusinessSignUpProps } from '../definition-extensions/business-sign-up.ext.props.autogenerated';

interface IBusinessSignUpExtensionProps extends IBusinessSignUpProps<{}> {
    extensionFormItems?: IFormItemExtension[];
};

interface IBusinessSignUpExtensionItem {
    wrapper: INodeProps;
    isAddressInput?: boolean;
    id: string;
    label: React.ReactNode;
    errorMessage: React.ReactNode;
    input?: React.ReactNode;
}

interface IBusinessExtensionState {
    isReady: boolean;
    extensionFormItems: IFormItemExtension[];
    addressHasErrors: boolean;
    showAddressErrors: boolean;
    hasSubmitErrors: boolean;
    isMissingFields: boolean;
};

interface IBusinessSignUpExtensionLocalAccount {
    FormWrapper: INodeProps;
    items: IBusinessSignUpExtensionItem[];
    buttons: React.ReactNode[];
    errorMessage: React.ReactNode[];
    disclaimer: React.ReactNode;
}

interface IFormItemExtension {
    id: string;
    type: InputTypeExtension;
    label: string;
    value: string;
    maxChars?: string;
    isRequired: boolean;
    pattern?: string;
    address?: Address;
    customClass?: string;
    fieldReference?: React.RefObject<HTMLInputElement>;
    placeholder?: string;
    stringList?: string[];
};

enum InputIDExtension {
    FirstName = 'FirstName',
    LastName = 'LastName',
    Title = 'Title',
    Email = 'CompanyEmail',
    CompanyName = 'CompanyName',
    JobTitle = 'JobTitle',
    Department = 'Department',
    Size = 'CompanySize',
    Address = 'CompanyAddress',
    Homepage = 'Homepage',
    Bank = 'Bank',
    BankAccountNumber = 'AccountNo',
    IBan = 'IBAN',
    VetOnlyAccept = 'VetOnly',
    InvoiceAddress = 'InvoiceAddress',
    VatNumber = 'VatNo',
    OrgNumber = 'OrgNo',
    Industry = 'Industry',
    AuthNumber = 'AuthorizationNo',
    DirectDebit = 'DirectDebit',
    Comment = 'Comment'
};

interface IBusinessSignUpExtensionLabelProps {
    id: string;
    forId: string;
    className: string;
    text: string;
};

interface IBusinessSignUpExtensionErrorProps {
    id?: string;
    className: string;
    type?: string;
    message?: string;
};

interface IBusinessSignUpExtensionButtonProps {
    id?: string;
    className: string;
    text: string;
    ariaLabel: string;
    disabled?: boolean;
    link?: string;
    telemetryContent: ITelemetryContent;
    onClick?(event: React.MouseEvent<HTMLElement>): void;
}

const SignUpExtensionButton: React.FC<IBusinessSignUpExtensionButtonProps> = ({
    id,
    className,
    text,
    ariaLabel,
    telemetryContent,
    disabled,
    link,
    onClick
}) => {
    const payLoad = getPayloadObject('click', telemetryContent, text);
    const attributes = getTelemetryAttributes(telemetryContent, payLoad);
    return (
        <Button
            id={id}
            className={className}
            aria-label={ariaLabel}
            disabled={disabled}
            onClick={onClick}
            role='button'
            aria-disabled={disabled}
            href={link}
            {...attributes}
        >
            {text}
        </Button>
    );
};

const SignUpExtensionError: React.FC<IBusinessSignUpExtensionErrorProps> = ({ id, className, type = 'page', message }) => {
    const errorClassName = `${className}__${type}-error`;

    return (
        <div id={id} className={classnames(errorClassName, 'error', `${type}Level`)} role='alert' aria-live='assertive' aria-hidden='true'>
            <p className={`${errorClassName}-text`}>{message}</p>
        </div>
    );
};

const SignUpExtensionLabel: React.FC<IBusinessSignUpExtensionLabelProps> = ({ id, forId, className, text }) => (
    <label id={`${id}_label`} className={`${className}-label`} htmlFor={forId}>
        {text}
    </label>
);

const renderInputSection = (
    inputs: IFormItemExtension[],
    className: string,
    needToReset: boolean,
    onInputChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void,
    showErrors: boolean,
    hasSubmitErrors: boolean,
    addressForm?: React.ReactNode,
    updateAddress?: (errors: boolean, currAddress?: Address) => void,
    errMessage?: string,
    highlightMissingFields?: boolean,
    isMissingFields?: boolean
): IBusinessSignUpExtensionItem[] => {
    return inputs.map((element, index) => {
        const { id, type, label, maxChars, isRequired, pattern, value, fieldReference, placeholder, stringList } = element;
        let customClass = element.customClass;

        let _errMessage = ``;
        if (isRequired) {
            _errMessage = `${label}${errMessage}`;
        }

        let labelText = label.replace("*", "");
        if (isRequired) {
            labelText = labelText + " *";
        }

        if (isRequired && type === InputTypeExtension.Address && (highlightMissingFields || isMissingFields)) {
            customClass = classnames(customClass, 'required')
        }

        const baseSignUpItem: IBusinessSignUpExtensionItem = {
            wrapper: {
                className: classnames(className, `${className}-${id}`, customClass)
            },
            id,
            label: <SignUpExtensionLabel {...{ id, forId: id, className, text: labelText }} />,
            errorMessage: value === '' && hasSubmitErrors && isRequired && false && ( // disable error message
                <SignUpExtensionError {...{ className, type: 'item', message: _errMessage }} />
            )
        };

        if (type === InputTypeExtension.Address) {
            if (addressForm) {
                baseSignUpItem.isAddressInput = true;
                baseSignUpItem.input = (
                    <>
                        {React.cloneElement(addressForm as React.ReactElement, {
                            updateForm: updateAddress,
                            hasError: showErrors,
                            isRequired: isRequired === undefined ? false : isRequired,
                            resetAddress: needToReset
                        })}
                    </>
                );
            } else {
                // If the address slot is not filled, remove this input from the form
                inputs.splice(index, 1);
            }
        } else {
            baseSignUpItem.input = (
                <SignUpExtensionInput
                    {...{
                        id,
                        type,
                        value,
                        maxLength: maxChars,
                        pattern,
                        className,
                        isRequired: isRequired === undefined ? false : isRequired,
                        onChange: onInputChange,
                        fieldReference,
                        placeholder,
                        stringList,
                        highlightMissingFields,
                        isMissingFields
                    }}
                />
            );
        }

        return baseSignUpItem;
    });
};

const BusinessSignUpItem: React.FC<IBusinessSignUpExtensionItem> = ({ wrapper, label, errorMessage, input, isAddressInput }) => {
    // Hide label and error message if rendering address section
    return (
        <Node {...wrapper}>
            {!isAddressInput && label}
            {!isAddressInput && errorMessage}
            {input}
        </Node>
    );
};

class BusinesSignUpView extends React.Component<IBusinessSignUpViewProps & IBusinessSignUpExtensionProps, IBusinessExtensionState> {
    // Used as default data
    public BusinessSignUpDefaultInputs: IFormItemExtension[] = [
        {
            id: InputIDExtension.FirstName,
            type: InputTypeExtension.Text,
            label: this.props.config.firstNameLabel ? this.props.config.firstNameLabel : this.props.resources.firstNameLabel,
            value: '',
            isRequired: this.props.config.firstNameRequired ? true : false,
            customClass: 'width-50',
            placeholder: this.props.config.firstNamePlaceholder ? this.props.config.firstNamePlaceholder : ''
        },
        {
            id: InputIDExtension.LastName,
            type: InputTypeExtension.Text,
            label: this.props.config.lastNameLabel ? this.props.config.lastNameLabel : this.props.resources.lastNameLabel,
            value: '',
            isRequired: this.props.config.lastNameRequired ? true : false,
            customClass: 'width-50',
            placeholder: this.props.config.lastNamePlaceholder ? this.props.config.lastNamePlaceholder : ''
        },
        {
            id: InputIDExtension.Title,
            type: InputTypeExtension.Text,
            label: this.props.config.titleLabel ? this.props.config.titleLabel : this.props.resources.titleLabel,
            value: '',
            isRequired: this.props.config.titleRequired ? true : false,
            customClass: 'width-50',
            placeholder: this.props.config.titlePlaceholder ? this.props.config.titlePlaceholder : ''
        },
        {
            id: InputIDExtension.Email,
            type: InputTypeExtension.Email,
            label: this.props.config.emailAddressLabel ? this.props.config.emailAddressLabel : this.props.resources.emailAddressLabel,
            value: '',
            isRequired: this.props.config.emailAddressRequired ? true : false,
            pattern: EmailRegex.defaultRegex.source,
            customClass: 'width-50',
            placeholder: this.props.config.emailAddressPlaceholder ? this.props.config.emailAddressPlaceholder : 'example@mail.com'
        },
        {
            id: InputIDExtension.CompanyName,
            type: InputTypeExtension.Text,
            label: this.props.config.companyLabel ? this.props.config.companyLabel : this.props.resources.companyLabel,
            value: '',
            isRequired: true, // default it to true, as company is always required
            customClass: 'width-50',
            placeholder: this.props.config.companyPlaceholder ? this.props.config.companyPlaceholder : ''
        },
        {
            id: InputIDExtension.Department,
            type: InputTypeExtension.Text,
            label: this.props.config.departmentLabel ? this.props.config.departmentLabel : this.props.resources.departmentLabel,
            value: '',
            isRequired: this.props.config.departmentRequired ? true : false,
            customClass: 'width-50',
            placeholder: this.props.config.departmentPlaceholder ? this.props.config.departmentPlaceholder : ''
        },
        {
            id: InputIDExtension.JobTitle,
            type: InputTypeExtension.Text,
            label: this.props.config.jobTitleLabel ? this.props.config.jobTitleLabel : this.props.resources.jobTitleLabel,
            value: '',
            isRequired: this.props.config.jobTitleRequired ? true : false,
            customClass: 'width-50',
            placeholder: this.props.config.jobTitlePlaceholder ? this.props.config.jobTitlePlaceholder : ''
        },
        {
            id: InputIDExtension.Size,
            type: InputTypeExtension.Text,
            label: this.props.config.companySizeLabel ? this.props.config.companySizeLabel : this.props.resources.companySizeLabel,
            value: '',
            isRequired: this.props.config.companySizeRequired ? true : false,
            customClass: 'width-50',
            placeholder: this.props.config.companySizePlaceholder ? this.props.config.companySizePlaceholder : ''
        },
        {
            id: InputIDExtension.Address,
            type: InputTypeExtension.Address,
            label: this.props.config.companyAddressLabel ? this.props.config.companyAddressLabel : this.props.resources.companyAddressLabel,
            value: '',
            isRequired: this.props.config.companyAddressRequired ? true : false,
            customClass: 'width-100'
        },
        {
            id: InputIDExtension.Homepage,
            type: InputTypeExtension.Text,
            label: this.props.config.homepageLabel ? this.props.config.homepageLabel : this.props.resources.homepageLabel,
            value: '',
            isRequired: this.props.config.homepageRequired ? true : false,
            customClass: 'width-100',
            placeholder: this.props.config.homepagePlaceholder ? this.props.config.homepagePlaceholder : 'www.company.com'
        },
        {
            id: InputIDExtension.Bank,
            type: InputTypeExtension.Text,
            label: this.props.config.bankLabel ? this.props.config.bankLabel : this.props.resources.bankLabel,
            value: '',
            isRequired: this.props.config.bankRequired ? true : false,
            customClass: 'width-50',
            placeholder: this.props.config.bankPlaceholder ? this.props.config.bankPlaceholder : ''
        },
        {
            id: InputIDExtension.BankAccountNumber,
            type: InputTypeExtension.Text,
            label: this.props.config.accountNumberLabel ? this.props.config.accountNumberLabel : this.props.resources.accountNumberLabel,
            value: '',
            isRequired: this.props.config.bankRequired ? true : false,
            customClass: 'width-50',
            placeholder: this.props.config.accountNumberPlaceholder ? this.props.config.accountNumberPlaceholder : ''
        },
        {
            id: InputIDExtension.IBan,
            type: InputTypeExtension.Text,
            label: this.props.config.ibanLabel ? this.props.config.ibanLabel : this.props.resources.ibanLabel,
            value: '',
            isRequired: this.props.config.ibanRequired ? true : false,
            customClass: 'width-100',
            placeholder: this.props.config.ibanPlaceholder ? this.props.config.ibanPlaceholder : ''
        },
        {
            id: InputIDExtension.VetOnlyAccept,
            type: InputTypeExtension.Checkbox,
            label: this.props.config.vetOnlyApprovalLabel ? this.props.config.vetOnlyApprovalLabel : this.props.resources.vetOnlyApprovalLabel,
            value: '',
            isRequired: this.props.config.vetOnlyApprovalRequired ? true : false,
            customClass: 'width-100',
            stringList: this.props.config.vetOnlyApprovalArray ? this.props.config.vetOnlyApprovalArray : ['Yes', 'No']
        },
        {
            id: InputIDExtension.InvoiceAddress,
            type: InputTypeExtension.TextArea,
            label: this.props.config.invoiceAddressLabel ? this.props.config.invoiceAddressLabel : this.props.resources.invoiceAddressLabel,
            value: '',
            isRequired: this.props.config.invoiceAddressRequired ? true : false,
            customClass: 'width-100',
            placeholder: this.props.config.invoiceAddressPlaceholder ? this.props.config.invoiceAddressPlaceholder : ''
        },
        {
            id: InputIDExtension.VatNumber,
            type: InputTypeExtension.Text,
            label: this.props.config.vatNumberLabel ? this.props.config.vatNumberLabel : this.props.resources.vatNumberLabel,
            value: '',
            isRequired: this.props.config.vatNumberRequired ? true : false,
            customClass: 'width-50',
            placeholder: this.props.config.vatNumberPlaceholder ? this.props.config.vatNumberPlaceholder : ''
        },
        {
            id: InputIDExtension.OrgNumber,
            type: InputTypeExtension.Text,
            label: this.props.config.orgNumberLabel ? this.props.config.orgNumberLabel : this.props.resources.orgNumberLabel,
            value: '',
            isRequired: this.props.config.orgNumberRequired ? true : false,
            customClass: 'width-50',
            placeholder: this.props.config.orgNumberPlaceholder ? this.props.config.orgNumberPlaceholder : ''
        },
        {
            id: InputIDExtension.Industry,
            type: InputTypeExtension.Dropdown,
            label: this.props.config.industryLabel ? this.props.config.industryLabel : this.props.resources.industryLabel,
            value: '',
            isRequired: this.props.config.industryRequired ? true : false,
            customClass: 'width-50',
            stringList: this.props.config.industryArray
                ? this.props.config.industryArray
                : [
                    'Petshop',
                    'Vet',
                    'Farrier',
                    'Farmer',
                    'Inseminator',
                    'Public institution',
                    'Wholesaler',
                    'Doctor/Dentist/Hospital',
                    'Other'
                ]
        },
        {
            id: InputIDExtension.AuthNumber,
            type: InputTypeExtension.Text,
            label: this.props.config.authorizationNumberLabel
                ? this.props.config.authorizationNumberLabel
                : this.props.resources.authorizationNumberLabel,
            value: '',
            isRequired: this.props.config.authorizationNumberRequired ? true : false,
            customClass: 'width-50',
            placeholder: this.props.config.authorizationNumberPlaceholder ? this.props.config.authorizationNumberPlaceholder : ''
        },
        {
            id: InputIDExtension.DirectDebit,
            type: InputTypeExtension.Checkbox,
            label: this.props.config.directDebitLabel ? this.props.config.directDebitLabel : this.props.resources.directDebitLabel,
            value: '',
            isRequired: this.props.config.directDebitRequired ? true : false,
            customClass: 'width-100',
            stringList: this.props.config.directDebitArray ? this.props.config.directDebitArray : ['Yes', 'No']
        },
        {
            id: InputIDExtension.Comment,
            type: InputTypeExtension.TextArea,
            label: this.props.config.commentLabel ? this.props.config.commentLabel : this.props.resources.commentLabel,
            value: '',
            isRequired: this.props.config.commentRequired ? true : false,
            customClass: 'width-100',
            placeholder: this.props.config.commentPlaceholder ? this.props.config.commentPlaceholder : ''
        }
    ];

    private readonly formChildReference: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();

    private readonly moduleClassName: string = 'ms-business-sign-up';

    private needsToReset: boolean = false;

    private readonly telemetryContent: ITelemetryContent;

    constructor(props: IBusinessSignUpViewProps & IBusinessSignUpExtensionProps) {
        super(props);

        this.state = {
            isReady: false,
            extensionFormItems: this.props.extensionFormItems || this._addedReference(),
            addressHasErrors: false,
            showAddressErrors: false,
            hasSubmitErrors: false,
            isMissingFields: false
        };
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    public componentDidMount(): void {
        this.formChildReference.current?.focus();
    }

    public shouldComponentUpdate(nextProps: IBusinessSignUpExtensionProps, nextState: IBusinessExtensionState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element {
        const { className, heading } = this.props.config;

        const businessSignUp: IModuleProps = {
            moduleProps: this.props,
            className: classnames(this.moduleClassName, className)
        };

        const ContainerWrapper: INodeProps = {
            className: `${this.moduleClassName}__container`
        };

        const headingNode: React.ReactNode = (
            <Text
                className={`${this.moduleClassName}__heading`}
                {...heading}
                editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
            />
        );

        const businessSignUpForm: IBusinessSignUpExtensionLocalAccount = this._renderForm();
        const { FormWrapper, buttons, items, errorMessage, disclaimer } = businessSignUpForm;

        return (
            <Module {...businessSignUp}>
                <Node {...ContainerWrapper}>
                    {headingNode}
                    <Node {...FormWrapper}>
                        {items &&
                            items.map((item: IBusinessSignUpExtensionItem) => {
                                return <BusinessSignUpItem key={item.id} {...item} />;
                            })}
                        {errorMessage && false && // disable error message
                            errorMessage.map((error: React.ReactNode, index: number) => {
                                return <React.Fragment key={index}>{error}</React.Fragment>;
                            })}
                        {buttons &&
                            buttons.map((button: React.ReactNode, index: number) => {
                                return <React.Fragment key={index}>{button}</React.Fragment>;
                            })}
                        {disclaimer}
                    </Node>
                </Node>
            </Module>
        );
    }

    /**
     * Handle the heading change event.
     * @param event - Content Editable Event.
     */
    public handleHeadingChange = (event: ContentEditableEvent): void => {
        this.props.config.heading.text = event.target.value;
    };

    private _renderForm(): IBusinessSignUpExtensionLocalAccount {
        const { config, resources, slots } = this.props;
        const { showAddressErrors, hasSubmitErrors, isMissingFields, extensionFormItems } = this.state;
        const {
            signUpButtonArialabel,
            signUpButtonText,
            cancelButtonArialabel,
            cancelButtonText,
            errorMessage,
            partialAddressErrorMessage,
            isRequiredErrorMessage
        } = resources;
        const addressForm = (slots && slots.addressSlot && slots.addressSlot.length > 0 && slots.addressSlot[0]) || undefined;
        const successPageURL =
            (config.confirmationLink && config.confirmationLink.linkUrl && config.confirmationLink.linkUrl.destinationUrl) ||
            getUrlSync('home', this.props.context.actionContext);

        const form = {
            FormWrapper: {
                className: `${this.moduleClassName}__form `
            },
            items: renderInputSection(
                extensionFormItems,
                `${this.moduleClassName}__form-item`,
                this.needsToReset,
                this._onInputChange,
                showAddressErrors,
                hasSubmitErrors,
                addressForm,
                this._onAddressChange,
                isRequiredErrorMessage,
                this.props.config.highlightMissingFields,
                isMissingFields
            ),
            buttons: [
                <SignUpExtensionButton
                    key='continue'
                    id='continue'
                    className={`${this.moduleClassName}__create-button`}
                    link={successPageURL}
                    ariaLabel={signUpButtonArialabel}
                    text={signUpButtonText}
                    //disabled={!isReady} // always show submit button
                    onClick={this._onSubmit}
                    telemetryContent={this.telemetryContent}
                />,
                <SignUpExtensionButton
                    key='cancel'
                    id='cancel'
                    className={`${this.moduleClassName}__cancel-button`}
                    ariaLabel={cancelButtonArialabel}
                    text={cancelButtonText}
                    onClick={this._onCancel}
                    telemetryContent={this.telemetryContent}
                />
            ],
            errorMessage: [
                hasSubmitErrors && <SignUpExtensionError key='error' id='error' className={this.moduleClassName} message={errorMessage} />,
                showAddressErrors && (
                    <SignUpExtensionError key='error' id='error' className={this.moduleClassName} message={partialAddressErrorMessage} />
                )
            ],
            disclaimer: config.disclaimer && (
                <RichTextComponent className={`${this.moduleClassName}__disclaimer`} text={config.disclaimer} />
            )
        };

        if (this.needsToReset) {
            this.needsToReset = false;
        }

        return form;
    }

    private readonly _onInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
        const newForm = this.state.extensionFormItems;
        newForm.forEach(item => {
            if (item.id === event.target.id || event.target.id.startsWith(item.id)) {
                item.value = event.target.value;
            }
        });

        // Check if form is ready, update state object with latest values, values have changed so reset hasSubmitErrros
        this.setState({
            isReady: this._isFormReady(),
            extensionFormItems: newForm,
            hasSubmitErrors: false
        });
    };

    private _addedReference(): IFormItemExtension[] {
        const newFormFields = this.BusinessSignUpDefaultInputs;
        newFormFields[0].fieldReference = this.formChildReference;
        return newFormFields;
    }

    private readonly _onAddressChange = (errors: boolean, currentAddress?: Address): void => {
        // Find address item
        const newForm = this.state.extensionFormItems;
        const addressItem = newForm.find(item => {
            return item.type === InputTypeExtension.Address;
        });

        // If found --> set to currentAddress
        if (addressItem) {
            addressItem.address = currentAddress;

            // Update state with new values and set errors to false if address is not required field
            this.setState({
                isReady: this._isFormReady(),
                extensionFormItems: newForm,
                addressHasErrors: errors,
                showAddressErrors: false
            });
        }
    };

    private readonly _isFormReady = (): boolean => {
        const { extensionFormItems } = this.state;

        for (const currentInput of extensionFormItems) {
            // Will return FALSE if:
            //  - input field is required
            //      IF is address AND addressHasErrors === true
            //      ELSE value is undefined OR empty
            //  OR
            //  - pattern provided and value does not match pattern

            if (currentInput.isRequired) {
                if (currentInput.type === InputTypeExtension.Address) {
                    if (this.state.addressHasErrors) {
                        return false;
                    }
                } else if (currentInput.value === undefined || currentInput.value === '') {
                    return false;
                }
            }
            if (currentInput.pattern !== undefined && currentInput.value.match(currentInput.pattern) === null) {
                return false;
            }
        }

        return true;
    };

    private readonly _onSubmit = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        if (this.state.addressHasErrors || this.state.hasSubmitErrors || !this.state.isReady) {
            if (this.state.addressHasErrors) {
                this.setState({
                    showAddressErrors: true,
                    isMissingFields: true
                });
            }

            if (this.state.hasSubmitErrors || !this.state.isReady) {
                this.setState({
                    hasSubmitErrors: true,
                    isMissingFields: true
                });
            }
            event.preventDefault();
            return;
        }

        const { context } = this.props;

        // Check for matching id and if not empty
        const firstName = this._getItem(InputIDExtension.FirstName);
        const lastName = this._getItem(InputIDExtension.LastName);
        const companyName = this._getItem(InputIDExtension.CompanyName);
        const companyEmail = this._getItem(InputIDExtension.Email);
        const companyAddress = this._getItem(InputIDExtension.Address, true);
        const jobTitle = this._getItem(InputIDExtension.JobTitle);
        const department = this._getItem(InputIDExtension.Department);
        const companySize = this._getItem(InputIDExtension.Size);
        const comments = this._getItem(InputIDExtension.Comment);
        const title = this._getItem(InputIDExtension.Title);
        const homepage = this._getItem(InputIDExtension.Homepage);
        const bank = this._getItem(InputIDExtension.Bank);
        const bankAccountNumber = this._getItem(InputIDExtension.BankAccountNumber);
        const iBan = this._getItem(InputIDExtension.IBan);
        const invoiceAddess = this._getItem(InputIDExtension.InvoiceAddress);
        const vatNumber = this._getItem(InputIDExtension.VatNumber);
        const orgNumber = this._getItem(InputIDExtension.OrgNumber);
        const industry = this._getItem(InputIDExtension.Industry);
        const authNumber = this._getItem(InputIDExtension.AuthNumber);
        const directDebit = this._getItem(InputIDExtension.DirectDebit);

        const _comment = `
            ${title?.label}: ${title?.value};
            ${homepage?.label} ${homepage?.value};
            ${bank?.label}: ${bank?.value};
            ${bankAccountNumber?.label}: ${bankAccountNumber?.value};
            ${iBan?.label}: ${iBan?.value};
            ${invoiceAddess?.label}: ${invoiceAddess?.value};
            ${vatNumber?.label}: ${vatNumber?.value};
            ${orgNumber?.label}: ${orgNumber?.value};
            ${industry?.label}: ${industry?.value};
            ${authNumber?.label}: ${authNumber?.value};
            ${directDebit?.label}: ${directDebit?.value};
            ${comments?.label}: ${comments?.value};
        `;

        // API gets email as part of the address object, but I do not use the email input from the address component, thus we need to copy it over
        if (companyEmail && companyEmail.value && companyAddress && companyAddress.address) {
            companyAddress.address.Email = companyEmail.value;
        }

        const newPartner: BusinessPartnerProspect = {
            FirstName: firstName && firstName.value,
            LastName: lastName && lastName.value,
            CompanyName: companyName && companyName.value,
            Address: companyAddress && companyAddress.address && companyAddress.address,
            JobTitle: jobTitle && jobTitle.value,
            Department: department && department.value,
            CompanySize: (companySize && Number.parseInt(companySize.value, 10)) || 0,
            Comments: _comment && _comment
        };

        // Create business partner request
        createProspectAsync({ callerContext: context.actionContext }, newPartner).catch(() => {
            this.setState({
                hasSubmitErrors: false,
                isReady: false
            });
        });
    };

    private readonly _onCancel = () => {
        // Reset values of the form
        const cleanForm = this.state.extensionFormItems;
        cleanForm.forEach(input => {
            input.value = '';
        });

        // Set reset flag so address input will get reset on next renderInputSection() call
        this.needsToReset = true;

        this.setState({
            isReady: false,
            extensionFormItems: cleanForm,
            addressHasErrors: true,
            showAddressErrors: false,
            hasSubmitErrors: false
        });
    };

    private readonly _getItem = (id: string, isAddress: boolean = false) => {
        if (isAddress) {
            return this.state.extensionFormItems.find(item => {
                return item.id === id && item.address;
            });
        }
        return this.state.extensionFormItems.find(item => {
            return item.id === id;
        });
    };
};

export default BusinesSignUpView;
